import { createSignal, type Accessor, type Setter } from 'solid-js';
import {
  FeatureHelper,
  type FeatureHelperOptions,
} from '@trimble/connect-feature-flag';
import type { ConnectFeatureFlagContext, FeatureFlagSource } from '../types';
import { getSourceFlagName, type FeatureFlag } from '../featureFlags';

/**
 * Feature flag source backed by the Connect `FeatureHelper` singleton from
 * `@trimble/connect-feature-flag`.
 *
 * Only answers for flags that have a Connect name, and reports every flag as disabled
 * until `initialize` has resolved. Scoped flags are evaluated against the
 * latest context.
 */
export class ConnectFeatureFlagProvider implements FeatureFlagSource {
  public readonly name = 'connect';
  private readonly helper: Accessor<FeatureHelper | undefined>;
  private readonly setHelper: Setter<FeatureHelper | undefined>;
  private readonly context: Accessor<ConnectFeatureFlagContext>;
  private readonly setContextSignal: Setter<ConnectFeatureFlagContext>;

  constructor(private readonly options?: FeatureHelperOptions) {
    const [helper, setHelper] = createSignal<FeatureHelper>();
    const [context, setContext] = createSignal<ConnectFeatureFlagContext>({});
    this.helper = helper;
    this.setHelper = setHelper;
    this.context = context;
    this.setContextSignal = setContext;
  }

  public async initialize(): Promise<void> {
    const helper = FeatureHelper.getInstance(this.options);
    await helper.initialize();
    this.setHelper(helper);
  }

  public setContext(context: ConnectFeatureFlagContext): void {
    this.setContextSignal(context);
  }

  public hasFlag(flag: FeatureFlag): boolean {
    const helper = this.helper();
    const name = getSourceFlagName(flag, 'connect');
    return (
      name !== undefined &&
      helper !== undefined &&
      helper.isFeatureEnabled(name, this.context())
    );
  }
}
